/**
 * Pure helpers for the Hudl page: labels, dates, yard lines and the
 * browser-side "save this file" step. No React in here so the smoke tests and
 * the page share one source of truth. The page-agnostic parts (errors, days,
 * saving a blob) live in `export-format.ts`, shared with the site-wide table
 * export, and are re-exported here for the Hudl page.
 */
import type {
  HudlCandidateEvent,
  HudlExportColumn,
  HudlExportField,
  HudlImportRecord,
  TeamEvent,
} from '../../lib/api-types';

import { formatDay } from './export-format';

export { formatDay, saveBlob, toMessage } from './export-format';

/** "Sep 21, 2026 · 4:05 PM" in the viewer's zone. */
export function formatWhen(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** `2026-09-21` → `Sep 21` — short enough for a phone-width picker. */
export function formatShortDay(value: string | null): string {
  if (value === null || value === '') return '';
  const date = new Date(value.length === 10 ? `${value}T12:00:00Z` : value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** Label for a game in a picker: `Week 3 vs Springboro · Sep 21`. */
export function eventOptionLabel(event: TeamEvent): string {
  const day = formatShortDay(event.scheduled_at);
  return day === '' ? event.name : `${event.name} · ${day}`;
}

export function candidateLabel(event: HudlCandidateEvent): string {
  const day = formatShortDay(event.date);
  return day === '' ? event.name : `${event.name} · ${day}`;
}

/** Detail line under the existing-game picker. */
export function candidateDetail(event: HudlCandidateEvent, isBest: boolean): string {
  const parts = [
    formatDay(event.date),
    plural(event.play_count, 'play'),
    event.status === 'closed' ? 'closed' : null,
    isBest ? 'best match for this file' : null,
  ].filter((part): part is string => part !== null && part !== '');
  return parts.join(' · ');
}

/** Games first, newest first — what a coach exports is almost always last Friday's game. */
export function sortEventsForExport(events: readonly TeamEvent[]): TeamEvent[] {
  return [...events].sort((left, right) => {
    if (left.event_type !== right.event_type) return left.event_type === 'game' ? -1 : 1;
    return right.scheduled_at.localeCompare(left.scheduled_at);
  });
}

/**
 * Hudl's yard line (-49..50, from the view of the team with the ball) as a
 * coach reads it: `-40` → "Own 40", `24` → "Opp 24", `50` → "50".
 */
export function formatHudlYardLine(value: number | null): string {
  if (value === null) return '—';
  if (value === 50 || value === -50) return '50';
  if (value < 0) return `Own ${Math.abs(value)}`;
  return `Opp ${value}`;
}

export function formatDownDistance(down: number | null, distance: number | null): string {
  if (down === null) return '—';
  const suffix = ['th', 'st', 'nd', 'rd'][down] ?? 'th';
  return distance === null ? `${down}${suffix}` : `${down}${suffix} & ${distance}`;
}

export function plural(count: number, one: string, many = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`;
}

export function importCountsLabel(record: HudlImportRecord): string {
  const { created, updated, inserted, skipped } = record.counts;
  const parts: string[] = [];
  const added = created + inserted;
  if (added > 0) parts.push(`${plural(added, 'new play', 'new plays')}`);
  if (updated > 0) parts.push(`${plural(updated, 'play', 'plays')} updated`);
  if (skipped > 0) parts.push(`${skipped} left out`);
  return parts.length === 0 ? 'No changes' : parts.join(' · ');
}

/** A fresh custom layout: the Hudl columns that map to something, in Hudl's order. */
export function defaultCustomColumns(
  hudlColumns: readonly { header: string; field: string | null }[],
  fields: readonly HudlExportField[]
): HudlExportColumn[] {
  const known = new Set(fields.map((field) => field.field));
  return hudlColumns
    .filter((column): column is { header: string; field: string } => column.field !== null)
    .filter((column) => known.size === 0 || known.has(column.field))
    .map((column) => ({ field: column.field, header: column.header }));
}

/** Fallback file name when the server did not send one. */
export function fallbackExportName(event: TeamEvent | null, extension: string): string {
  const day = event?.scheduled_at.slice(0, 10) ?? new Date().toISOString().slice(0, 10);
  const who = (event?.opponent_name ?? event?.name ?? 'Game').trim().replace(/\s+/g, '_');
  return `PlaylistData_${day}_${who}.${extension}`;
}
